import React, { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { ChevronDown, X } from 'lucide-react';
import moment from 'moment';
import { Calendar } from '../UI/calendar';

type ComboCellProps = {
  row: any;
  debouncedSearch: string;
  highlightText: (text: any, searchTerm: any, delay?: number) => any;
  handleStatusColor: (key: any, val: any) => any;
  handleUpdateProject: (row: any, column: string, value: string) => void;
  status: any;
  date: any;
  dateKey: string;
  statusKey: string;
  valueKey: string;
  dropdownItems: {
    name: string;
    value: string;
  }[];
  handleChangeDate(obj: any, field: string, v: Date | undefined): void;
  disabled?: boolean;
};

const ComboCell = (props: ComboCellProps) => {
  const {
    row,
    debouncedSearch,
    highlightText,
    handleStatusColor,
    handleUpdateProject,
    status,
    date,
    dateKey,
    statusKey,
    valueKey,
    dropdownItems,
    handleChangeDate,
    disabled = false,
  } = props;

  const [open, setOpen] = useState<boolean>(false);
  const [dateOpen, setDateOpen] = useState<boolean>(false);

  const date_format = !date
    ? undefined
    : moment(date).isValid()
      ? moment(date).toDate()
      : undefined;

  const handleShowInvoiceStatus = (val: any) => {
    const lower_val = val ? val.toLowerCase() : '';
    let return_string = '';
    switch (lower_val) {
      case 'no':
        return_string = 'Not Paid';
        break;
      case 'yes':
        return_string = 'Paid';
        break;
      default:
        return_string = val;
    }
    return return_string;
  };

  return (
    <div className="flex flex-col items-start space-y-0.5">
      <Popover
        open={open}
        onOpenChange={(val) => {
          if (disabled) return;
          setOpen(val);
        }}
      >
        <PopoverTrigger
          data-qa={`projects_row_${row?.original?.id}_${statusKey}_status_trigger`}
          asChild
        >
          <div
            className={`${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'} inline-flex items-center gap-2 whitespace-nowrap rounded-md text-sm ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 h-auto p-0 justify-start font-normal hover:bg-transparent ${handleStatusColor(statusKey, status)}`}
          >
            {statusKey === 'invoicePaid' ? (
              <span>
                {highlightText(handleShowInvoiceStatus(status || 'select'), debouncedSearch)}
              </span>
            ) : (
              <span>{highlightText(status || 'select', debouncedSearch)}</span>
            )}
            <ChevronDown size={16} />
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[501] min-w-[8rem] overflow-hidden rounded-md p-1 text-popover-foreground data-[state=open]:animate-animate-in data-[state=closed]:animate-animate-out data-[side=bottom]:animate-slide-in-from-top-2 data-[side=left]:animate-slide-in-from-right-2 data-[side=right]:animate-slide-in-from-left-2 data-[side=top]:animate-slide-in-from-bottom-2 w-48 bg-background border border-input shadow-md"
        >
          <div className="flex flex-col gap-1 max-h-[400px] overflow-y-auto overflow-x-hidden">
            {dropdownItems &&
              dropdownItems?.length > 0 &&
              dropdownItems?.map((item, index) => {
                return (
                  <div
                    data-qa={`projects_row_${row?.original?.id}_${statusKey}_status_item_${item?.value}`}
                    key={index}
                    className={`relative flex select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 cursor-pointer ${handleStatusColor(statusKey, item?.value)}`}
                    onClick={() => {
                      if (disabled) return;
                      handleUpdateProject(row?.original, valueKey, item?.value);
                      setOpen(false);
                    }}
                  >
                    {item?.name || ''}
                  </div>
                );
              })}
          </div>
        </PopoverContent>
      </Popover>

      <Popover
        open={dateOpen}
        onOpenChange={(val) => {
          if (disabled) return;
          setDateOpen(val);
        }}
      >
        <PopoverTrigger
          data-qa={`projects_row_${row?.original?.id}_${statusKey}_date_trigger`}
          asChild
        >
          <div className="group/date flex items-center gap-1">
            <button
              type="button"
              disabled={disabled}
              className="hover:text-teal transition disabled:cursor-not-allowed disabled:opacity-60"
            >
              {highlightText(
                date_format ? moment(date_format).format('MM/DD/YY') : 'MM/DD/YY',
                debouncedSearch
              )}
            </button>
            {date_format && (
              <button
                disabled={disabled}
                onMouseDown={(e) => {
                  if (disabled) return;
                  e.preventDefault();
                  e.stopPropagation();
                  handleChangeDate(row?.original, dateKey, undefined);
                  setDateOpen(false);
                }}
                data-qa={`projects_row_${row?.original?.id}_${statusKey}_date_clear_trigger`}
                className="h-3.5 w-3.5 rounded text-muted-foreground opacity-60 transition-opacity group-hover/date:opacity-100 hover:bg-muted hover:text-foreground hover:[&_svg]:text-destructive"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="z-[1000] rounded-md border border-input bg-popover text-popover-foreground shadow-md outline-none pointer-events-auto data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 w-auto p-0"
        >
          <Calendar
            data-qa={`projects_row_${row?.original1?.id}_project_${dateKey}_date_calendar`}
            selected={date_format}
            mode="single"
            className="rounded-md border-0 p-[12px]"
            onSelect={(v) => {
              if (disabled) return;
              handleChangeDate(row?.original, dateKey, v);
              setDateOpen(false);
            }}
          />
          {date_format && (
            <div className="border-t px-3 py-2">
              <button
                type="button"
                className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-destructive"
                onClick={() => {
                  if (disabled) return;
                  handleChangeDate(row?.original, dateKey, undefined);
                  setDateOpen(false);
                }}
              >
                <X className="h-3 w-3" />
                Clear
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default ComboCell;
